import React from 'react';
import { Box, Divider, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DataCell } from '../../../../common/components/DataCell';
import { useBannerSize } from '../../../../common/hooks/use-banner-size';
import { formatDynamicDecimal } from '../../../../utils/common/common-utils';
import { COLOR_ADD_RATES, COLOR_BLUE_DARK, COLOR_DIVIDER, COLOR_NUMBER } from './banner-colors';

export function BannerDivider({ borderColor = COLOR_DIVIDER }: { borderColor?: string }) {
  return <Divider orientation="vertical" h="50px" borderColor={borderColor} />;
}

export function CumulativePowerCell({ kwhConsumed }: { kwhConsumed: number }) {
  const { t } = useTranslation();
  return (
    <Box flex={1} textAlign="center" pr={2}>
      <DataCell
        value={formatDynamicDecimal(kwhConsumed)}
        suffix="kWh"
        label={t('EVSE.session energy')}
        valueTestId="cumulative-power-card"
      />
    </Box>
  );
}

export function ChargeSpeedCell({ powerFormatted }: { powerFormatted: string }) {
  const { t } = useTranslation();
  return (
    <Box flex={1} textAlign="center" px={1}>
      <DataCell value={powerFormatted} suffix="kW" label={t('EVSE.charge speed')} valueTestId="current-power-card" />
    </Box>
  );
}

export function TariffCell({ hasTariff, rate = '$0.00' }: { hasTariff: boolean; rate?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: siteId } = useParams<{ id: string }>();
  const { fontSize } = useBannerSize();

  const formattedRate = (
    <Box fontSize={fontSize} _dark={{ color: 'white' }}>
      <Box as="span" fontSize="0.6em" verticalAlign="baseline">
        {rate.slice(0, 1)}
      </Box>
      {rate.slice(1)}
    </Box>
  );

  if (hasTariff) {
    return (
      <Box flex={1} textAlign="center" px={2}>
        {formattedRate}
        <Text fontSize="12px">{t('Common.cost')}</Text>
      </Box>
    );
  }

  return (
    <Box flex={1} textAlign="center" px={2}>
      {formattedRate}
      <Text
        as="button"
        fontSize="14px"
        color={COLOR_ADD_RATES}
        _dark={{ color: COLOR_BLUE_DARK }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(
            `../../account/tariff/new/home?direction=forward&backURL=/sites/${siteId}/home/device-status?direction=back`
          );
        }}
        data-testid="add-rates-link"
      >
        {t('EVSE.add rates')}
      </Text>
    </Box>
  );
}

export function StatusCell({ status, color = COLOR_NUMBER }: { status: string; color?: string }) {
  return (
    <Box flex={1} textAlign="center">
      <Text fontSize="lg" color={color}>
        {status}
      </Text>
    </Box>
  );
}

export function EnergyUsedCell({ energy = '0.0' }: { energy?: string }) {
  const { t } = useTranslation();
  const { fontSize } = useBannerSize();
  return (
    <Box flex={1} textAlign="center">
      <Box fontSize={fontSize} _dark={{ color: 'white' }}>
        {energy}
        <Box as="span" fontSize="14px" fontWeight="normal">
          {' '}
          kWh
        </Box>
      </Box>
      <Text fontSize="12px">{t('EVSE.energy used')}</Text>
    </Box>
  );
}
